import { useMemo } from 'react';

import { tsrestClient } from '@portal/provider/tsrest';

/**
 * Global hook to access user authentication status and user information.
 *
 * This hook fetches user status from the backend and provides:
 * - Current user's username
 * - Admin status (is_admin field from backend)
 * - Loading and error states
 * - Helper functions for common admin checks
 *
 * @example
 * ```tsx
 * const MyComponent = () => {
 *   const { user, isAdmin, isSuperAdmin, canAccessAdminFeatures } = useAuthStatus();
 *
 *   if (isSuperAdmin) {
 *     return <div>You are the super admin user</div>;
 *   }
 *
 *   if (canAccessAdminFeatures) {
 *     return <div>You can access admin features</div>;
 *   }
 *
 *   return <div>Standard user view</div>;
 * };
 * ```
 */
export const useAuthStatus = () => {
  const userStatusQuery = tsrestClient.auth.status.useQuery(
    ['user'],
    {
      headers: {
        authorization: localStorage.getItem('token') || '',
      },
    },
    {
      refetchInterval: 30000,
      retry: false,
    }
  );

  const user = userStatusQuery.data?.body.username || '';
  const isAdmin = userStatusQuery.data?.body.is_admin || false;
  const isLoading = userStatusQuery.isLoading;
  const isError = userStatusQuery.isError;
  const error = userStatusQuery.error;

  // Memoize the result to prevent unnecessary re-renders
  const result = useMemo(
    () => ({
      user,
      isAdmin,
      isLoading,
      isError,
      error,
      // Helper function to check if user is the super admin (username === 'admin')
      isSuperAdmin: user === 'admin',
      // Helper function to check if user can access admin features (is_admin or super admin)
      canAccessAdminFeatures: isAdmin || user === 'admin',
    }),
    [user, isAdmin, isLoading, isError, error]
  );

  return result;
};
